'use client';

import { type CSSProperties, type UIEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Eye, Loader2, Maximize2, Settings2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatRowsCounterLabel, formatRowsLoadMoreLabel } from '@/hooks/use-lazy-dataset-rows';
import { useToast } from '@/hooks/use-toast';
import { buildImageProxySrc, normalizeImageUrl } from '@/lib/imageProxy';
import {
  classifyCellValue,
  isExpandable,
  isImageColumnType,
  toCompactDisplay,
  type PreviewCellKind,
} from '@/lib/previewTableValue';
import { formatTableCellValue } from '@/lib/tableValue';
import { cn } from '@/lib/utils';

export interface PreviewDataTableProps {
  tableId: string;
  rows: Array<Record<string, unknown>>;
  columns?: string[];
  columnTypes?: Record<string, string>;
  emptyMessage: string;
  variant?: 'full' | 'compact';
  enableColumnControls?: boolean;
  pinFirstColumn?: boolean;
  normalizeValue?: (value: unknown) => unknown;
  showRowCount?: boolean;
  maxHeightClassName?: string;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => Promise<void> | void;
  loadedCount?: number;
  totalCount?: number | null;
  loadMoreLabel?: string;
  loadMoreError?: string | null;
}

type ExpandedCellState = {
  column: string;
  value: string;
};

type RowDetailsState = {
  rowIndex: number;
  row: Record<string, unknown>;
};

type ImagePreviewState = {
  column: string;
  proxySrc: string;
  originalUrl: string;
};

const FULL_CLAMP_STYLE: CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
  overflow: 'hidden',
};

const COMPACT_CLAMP_STYLE: CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
};

const kindWidthClass: Record<PreviewCellKind, string> = {
  empty: 'w-40 max-w-[10rem]',
  number: 'w-24 max-w-[6rem]',
  text: 'w-48 max-w-[12rem]',
  url: 'w-56 max-w-[14rem]',
  image: 'w-64 max-w-[16rem]',
};

export function PreviewDataTable({
  tableId,
  rows,
  columns: providedColumns,
  columnTypes,
  emptyMessage,
  variant = 'full',
  enableColumnControls = true,
  pinFirstColumn = true,
  normalizeValue,
  showRowCount,
  maxHeightClassName,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  loadedCount,
  totalCount = null,
  loadMoreLabel,
  loadMoreError = null,
}: PreviewDataTableProps) {
  const { toast } = useToast();
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [expandedCell, setExpandedCell] = useState<ExpandedCellState | null>(null);
  const [rowDetails, setRowDetails] = useState<RowDetailsState | null>(null);
  const [imagePreview, setImagePreview] = useState<ImagePreviewState | null>(null);
  const [imagePreviewLoadFailed, setImagePreviewLoadFailed] = useState(false);
  const [hiddenImagePreviewSrcs, setHiddenImagePreviewSrcs] = useState<Record<string, true>>({});
  const lazyLoadSentinelRef = useRef<HTMLDivElement | null>(null);

  const rowColumns = useMemo(() => {
    if (!rows.length) {
      return [];
    }
    const orderedKeys = new Set<string>();
    rows.forEach((row) => {
      Object.keys(row).forEach((key) => orderedKeys.add(key));
    });
    return Array.from(orderedKeys);
  }, [rows]);

  const columns = useMemo(() => {
    if (!providedColumns?.length) {
      return rowColumns;
    }
    const normalizedProvided = providedColumns.filter((column) => column.trim().length > 0);
    if (!normalizedProvided.length) {
      return rowColumns;
    }
    const unique = new Set<string>(normalizedProvided);
    rowColumns.forEach((column) => unique.add(column));
    return Array.from(unique);
  }, [providedColumns, rowColumns]);

  const normalize = useCallback(
    (value: unknown) => (normalizeValue ? normalizeValue(value) : value),
    [normalizeValue],
  );

  useEffect(() => {
    setVisibleColumns(columns);
  }, [tableId, columns]);

  useEffect(() => {
    setHiddenImagePreviewSrcs({});
    setImagePreview(null);
    setImagePreviewLoadFailed(false);
  }, [tableId]);

  const columnKinds = useMemo(() => {
    const kinds: Record<string, PreviewCellKind> = {};
    columns.forEach((column) => {
      const columnType = columnTypes?.[column];
      let detected: PreviewCellKind = 'empty';
      for (const row of rows) {
        const normalized = normalize(row[column]);
        const kind = classifyCellValue(normalized, column, columnType);
        if (kind !== 'empty') {
          detected = kind;
          break;
        }
      }
      kinds[column] = detected === 'empty' ? (isImageColumnType(columnType) ? 'image' : 'text') : detected;
    });
    return kinds;
  }, [columnTypes, columns, rows, normalize]);

  const canManageColumns = variant === 'full' && enableColumnControls && columns.length > 1;
  const shouldShowRowCount = showRowCount ?? variant === 'full';
  const renderedColumns = useMemo(() => {
    if (!canManageColumns) {
      return columns;
    }
    const filtered = columns.filter((column) => visibleColumns.includes(column));
    if (filtered.length > 0) {
      return filtered;
    }
    return columns.slice(0, 1);
  }, [canManageColumns, columns, visibleColumns]);

  const hiddenColumns = useMemo(
    () => columns.filter((column) => !renderedColumns.includes(column)),
    [columns, renderedColumns],
  );

  const showRowDetails = variant === 'full' && hiddenColumns.length > 0;
  const canLazyLoad = variant === 'full' && typeof onLoadMore === 'function';
  const pinnedColumn = variant === 'full' && pinFirstColumn ? renderedColumns[0] : undefined;
  const clampStyle = variant === 'compact' ? COMPACT_CLAMP_STYLE : FULL_CLAMP_STYLE;
  const imagePreviewSizeClass = variant === 'compact' ? 'h-10 w-10' : 'h-14 w-14';
  const showScrollHint = variant === 'full' && renderedColumns.length > 6;
  const tableMinWidthRem = variant === 'compact'
    ? Math.max(renderedColumns.length * 8, 24)
    : Math.max(renderedColumns.length * 9, 36);

  const closeExpandedCell = () => setExpandedCell(null);
  const closeRowDetails = () => setRowDetails(null);
  const closeImagePreview = () => {
    setImagePreview(null);
    setImagePreviewLoadFailed(false);
  };

  const copyText = async (value: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: successMessage });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Не удалось скопировать',
        description: 'Не удалось скопировать в буфер обмена.',
      });
    }
  };

  const toggleColumn = (column: string, checked: boolean) => {
    setVisibleColumns((current) => {
      if (checked) {
        if (current.includes(column)) {
          return current;
        }
        return columns.filter((candidate) => candidate === column || current.includes(candidate));
      }
      if (current.length <= 1) {
        return current;
      }
      return current.filter((candidate) => candidate !== column);
    });
  };

  const hideImagePreviewSrc = useCallback((src: string) => {
    setHiddenImagePreviewSrcs((current) => {
      if (current[src]) {
        return current;
      }
      return { ...current, [src]: true };
    });
  }, []);

  const rowDetailsEntries = useMemo(() => {
    if (!rowDetails) {
      return [];
    }
    return hiddenColumns.map((column) => {
      const normalizedValue = normalize(rowDetails.row[column]);
      return {
        column,
        value: formatTableCellValue(normalizedValue),
      };
    });
  }, [hiddenColumns, normalize, rowDetails]);

  const rowJson = useMemo(() => {
    if (!rowDetails) {
      return '';
    }
    const normalizedRow = Object.fromEntries(
      columns.map((column) => [column, normalize(rowDetails.row[column])]),
    );
    return JSON.stringify(normalizedRow, null, 2);
  }, [columns, normalize, rowDetails]);

  const displayedCount = loadedCount ?? rows.length;
  const rowCountLabel = formatRowsCounterLabel(displayedCount, totalCount);
  const resolvedLoadMoreLabel = loadMoreLabel ?? formatRowsLoadMoreLabel(displayedCount, totalCount);
  const shouldShowLoadMoreButton = canLazyLoad && (hasMore || isLoadingMore || Boolean(loadMoreError));
  const shouldObserveViewport = canLazyLoad && hasMore && !maxHeightClassName;

  const triggerLoadMore = useCallback(() => {
    if (!onLoadMore || isLoadingMore || (!hasMore && !loadMoreError)) {
      return;
    }
    void onLoadMore();
  }, [hasMore, isLoadingMore, loadMoreError, onLoadMore]);

  const handleContainerScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (!canLazyLoad || !maxHeightClassName) {
        return;
      }
      if (isLoadingMore || !hasMore) {
        return;
      }
      const element = event.currentTarget;
      const thresholdPx = 120;
      const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - thresholdPx;
      if (isNearBottom) {
        triggerLoadMore();
      }
    },
    [canLazyLoad, hasMore, isLoadingMore, maxHeightClassName, triggerLoadMore],
  );

  useEffect(() => {
    if (!shouldObserveViewport || !lazyLoadSentinelRef.current) {
      return;
    }
    const sentinel = lazyLoadSentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          triggerLoadMore();
        }
      },
      { root: null, rootMargin: '240px 0px 240px 0px', threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [shouldObserveViewport, triggerLoadMore]);

  if (!rows.length || !columns.length) {
    return <p className="text-sm text-[var(--site-ink-muted)]">{emptyMessage}</p>;
  }

  return (
    <div className="min-w-0 space-y-3 overflow-x-hidden">
      {canManageColumns || shouldShowRowCount ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          {shouldShowRowCount ? (
            <p className="text-xs text-[var(--site-ink-muted)]">{rowCountLabel}</p>
          ) : (
            <span />
          )}
          {canManageColumns ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm">
                  <Settings2 className="h-4 w-4" />
                  Столбцы ({renderedColumns.length}/{columns.length})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Видимые столбцы</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {columns.map((column) => {
                  const isVisible = renderedColumns.includes(column);
                  const isOnlyVisible = isVisible && renderedColumns.length === 1;
                  return (
                    <DropdownMenuCheckboxItem
                      key={`${tableId}-column-${column}`}
                      checked={isVisible}
                      disabled={isOnlyVisible}
                      onCheckedChange={(checked) => toggleColumn(column, checked === true)}
                      onSelect={(event) => event.preventDefault()}
                    >
                      <span className="truncate" title={column}>
                        {column}
                      </span>
                    </DropdownMenuCheckboxItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    setVisibleColumns(columns);
                  }}
                >
                  Сбросить столбцы
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      ) : null}

      <Table
        className="table-fixed text-sm"
        containerClassName={cn(maxHeightClassName)}
        onContainerScroll={maxHeightClassName ? handleContainerScroll : undefined}
        style={{ minWidth: `${tableMinWidthRem}rem` }}
      >
        <TableHeader>
          <TableRow>
            {renderedColumns.map((column) => {
              const kind = columnKinds[column] ?? 'text';
              const isPinned = column === pinnedColumn;
              return (
                <TableHead
                  key={`${tableId}-head-${column}`}
                  className={cn(
                    'sticky top-0 whitespace-nowrap text-xs font-semibold text-[var(--site-ink-muted)]',
                    kindWidthClass[kind],
                    isPinned ? 'left-0 z-30 bg-[var(--site-surface-inset)]' : 'z-20 bg-[var(--site-surface-inset)]',
                  )}
                >
                  <span className="block truncate" title={column}>
                    {column}
                  </span>
                </TableHead>
              );
            })}
            {showRowDetails ? (
              <TableHead className="sticky top-0 z-20 w-36 max-w-[9rem] whitespace-nowrap bg-[var(--site-surface-inset)] text-xs font-semibold text-[var(--site-ink-muted)]">
                Скрытые поля
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={`${tableId}-row-${rowIndex}`} className="group">
              {renderedColumns.map((column) => {
                const normalizedValue = normalize(row[column]);
                const fullValue = formatTableCellValue(normalizedValue);
                const compactValue = toCompactDisplay(normalizedValue);
                const columnType = columnTypes?.[column];
                const kind = columnKinds[column] ?? classifyCellValue(normalizedValue, column, columnType);
                const isPinned = column === pinnedColumn;
                const expandable = isExpandable(normalizedValue);
                const title = fullValue === '—' ? undefined : fullValue;
                const imageOriginalUrl = kind === 'image' ? normalizeImageUrl(normalizedValue) : null;
                const imageProxySrc = kind === 'image' ? buildImageProxySrc(normalizedValue) : null;
                const shouldShowImagePreview =
                  kind === 'image' &&
                  typeof imageProxySrc === 'string' &&
                  imageProxySrc.length > 0 &&
                  typeof imageOriginalUrl === 'string' &&
                  imageOriginalUrl.length > 0 &&
                  !hiddenImagePreviewSrcs[imageProxySrc];

                return (
                  <TableCell
                    key={`${tableId}-cell-${rowIndex}-${column}`}
                    className={cn(
                      'align-top',
                      kindWidthClass[kind],
                      kind === 'number'
                        ? 'whitespace-nowrap text-right font-medium tabular-nums'
                        : 'whitespace-normal break-words [overflow-wrap:anywhere]',
                      isPinned ? 'sticky left-0 z-20 bg-background group-hover:bg-muted/50' : undefined,
                    )}
                    title={title}
                  >
                    {kind === 'number' ? (
                      fullValue
                    ) : (
                      <div className="flex items-start justify-between gap-1.5">
                        <div
                          className={cn(
                            'min-w-0',
                            kind === 'image' ? 'flex items-start gap-2' : 'space-y-2',
                          )}
                        >
                          {shouldShowImagePreview ? (
                            <button
                              type="button"
                              className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                              onClick={() => {
                                if (!imageProxySrc || !imageOriginalUrl) return;
                                setImagePreview({
                                  column,
                                  proxySrc: imageProxySrc,
                                  originalUrl: imageOriginalUrl,
                                });
                                setImagePreviewLoadFailed(false);
                              }}
                            >
                              <img
                                src={imageProxySrc}
                                alt={`Изображение из столбца ${column}`}
                                className={cn(
                                  imagePreviewSizeClass,
                                  'rounded-md border border-[color:var(--site-border-soft)] bg-[var(--site-surface-inset)] object-cover',
                                )}
                                loading="lazy"
                                decoding="async"
                                referrerPolicy="no-referrer"
                                onError={() => {
                                  if (imageProxySrc) {
                                    hideImagePreviewSrc(imageProxySrc);
                                  }
                                }}
                              />
                            </button>
                          ) : null}
                          <span
                            className={cn(
                              kind === 'url' || kind === 'image' ? 'font-mono text-xs text-[var(--site-ink)]' : 'text-[var(--site-ink)]',
                              kind === 'image' ? 'block flex-1 min-w-0' : undefined,
                            )}
                            style={clampStyle}
                          >
                            {compactValue}
                          </span>
                        </div>
                        {expandable ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0 text-[var(--site-ink-muted)] hover:text-[var(--site-ink)]"
                            onClick={() => setExpandedCell({ column, value: fullValue })}
                          >
                            <Maximize2 className="h-3.5 w-3.5" />
                            <span className="sr-only">Развернуть полное значение</span>
                          </Button>
                        ) : null}
                      </div>
                    )}
                  </TableCell>
                );
              })}

              {showRowDetails ? (
                <TableCell className="w-36 max-w-[9rem] whitespace-nowrap align-top">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-[var(--site-ink)] hover:text-[var(--site-ink-strong)]"
                    onClick={() => setRowDetails({ rowIndex, row })}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Показать скрытые
                  </Button>
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {showScrollHint ? (
        <p className="text-xs text-[var(--site-ink-muted)]">Прокрутите горизонтально, чтобы увидеть все столбцы.</p>
      ) : null}

      {shouldObserveViewport ? <div ref={lazyLoadSentinelRef} className="h-1 w-full" /> : null}

      {canLazyLoad ? (
        <div className="flex min-h-8 flex-wrap items-center justify-between gap-2">
          {loadMoreError ? (
            <p className="text-xs text-rose-600">{loadMoreError}</p>
          ) : (
            <p className="text-xs text-[var(--site-ink-muted)]">
              {hasMore ? 'Доступны дополнительные строки.' : 'Все строки загружены.'}
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={triggerLoadMore}
            disabled={isLoadingMore || (!hasMore && !loadMoreError)}
            className={shouldShowLoadMoreButton ? undefined : 'invisible'}
          >
            {isLoadingMore ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Загрузка…
              </>
            ) : loadMoreError ? (
              'Повторить'
            ) : (
              resolvedLoadMoreLabel
            )}
          </Button>
        </div>
      ) : null}

      <Dialog open={expandedCell !== null} onOpenChange={(open) => (!open ? closeExpandedCell() : undefined)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
          <DialogHeader>
            <DialogTitle>Значение ячейки</DialogTitle>
            <DialogDescription>Столбец: {expandedCell?.column ?? '—'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="max-h-[58vh] overflow-auto rounded-md border bg-[var(--site-surface-inset)] p-3">
              <pre className="whitespace-pre-wrap break-words text-xs text-[var(--site-ink)] [overflow-wrap:anywhere]">
                {expandedCell?.value ?? ''}
              </pre>
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!expandedCell?.value) return;
                  copyText(expandedCell.value, 'Значение ячейки скопировано');
                }}
              >
                <Copy className="h-4 w-4" />
                Скопировать значение
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={imagePreview !== null} onOpenChange={(open) => (!open ? closeImagePreview() : undefined)}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-hidden">
          <DialogHeader>
            <DialogTitle>Превью изображения</DialogTitle>
            <DialogDescription>Столбец: {imagePreview?.column ?? '—'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex max-h-[65vh] items-center justify-center overflow-auto rounded-md border bg-[var(--site-surface-inset)] p-3">
              {imagePreview && !imagePreviewLoadFailed ? (
                <img
                  src={imagePreview.proxySrc}
                  alt={`Изображение из столбца ${imagePreview.column} в полном размере`}
                  className="max-h-[62vh] w-auto max-w-full object-contain"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  onError={() => setImagePreviewLoadFailed(true)}
                />
              ) : (
                <p className="text-sm text-[var(--site-ink-muted)]">Превью недоступно.</p>
              )}
            </div>
            <div className="rounded-md border bg-[var(--site-surface-inset)] p-2 text-xs font-mono text-[var(--site-ink)] [overflow-wrap:anywhere]">
              {imagePreview?.originalUrl ?? ''}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!imagePreview?.originalUrl) return;
                  copyText(imagePreview.originalUrl, 'URL изображения скопирован');
                }}
              >
                <Copy className="h-4 w-4" />
                Скопировать URL
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!imagePreview?.originalUrl) return;
                  window.open(imagePreview.originalUrl, '_blank', 'noopener,noreferrer');
                }}
              >
                Открыть оригинал
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={rowDetails !== null} onOpenChange={(open) => (!open ? closeRowDetails() : undefined)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
          <DialogHeader>
            <DialogTitle>Скрытые поля</DialogTitle>
            <DialogDescription>
              Строка {rowDetails ? rowDetails.rowIndex + 1 : '—'}, значения скрытых столбцов.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="max-h-[58vh] overflow-auto rounded-md border">
              {rowDetailsEntries.length ? (
                <div className="divide-y">
                  {rowDetailsEntries.map((entry) => (
                    <div key={`${tableId}-hidden-${entry.column}`} className="space-y-1 p-3">
                      <div className="text-xs font-semibold text-[var(--site-ink-muted)]">{entry.column}</div>
                      <div className="whitespace-pre-wrap break-words text-sm text-[var(--site-ink)] [overflow-wrap:anywhere]">
                        {entry.value}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-3 text-sm text-[var(--site-ink-muted)]">Для этой строки нет скрытых значений.</div>
              )}
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!rowJson) return;
                  copyText(rowJson, 'JSON строки скопирован');
                }}
              >
                <Copy className="h-4 w-4" />
                Скопировать JSON строки
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
